import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Paper,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  Sync as SyncIcon,
  Cloud as CloudIcon,
  CloudUpload as CloudUploadIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Apps as AppsIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as TuneIcon,
  VpnKey as VpnKeyIcon,
  History as HistoryIcon,
  Login as LoginIcon,
  Assessment as AssessmentIcon,
  Link as LinkIcon,
  AccountTree as AccountTreeIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { hasMinRole, isReadOnly, ROLES } from '../../utils/roles';

import './Sidebar.css';

const drawerWidth = 280;
const collapsedWidth = 72;

const Sidebar = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);

  const [isAppAdmin, setIsAppAdmin] = useState(false);
  const [isCustomerAdmin, setIsCustomerAdmin] = useState(false);
  const [customerConfig, setCustomerConfig] = useState(null);
  const [saasConnected, setSaasConnected] = useState(false);
  const [impersonation, setImpersonation] = useState(null);
  const [userDetails, setUserDetails] = useState({
    username: 'Admin',
    firstName: '',
    role: '',
    isAdmin: false
  });

  useEffect(() => {
    const appAdminDetails = localStorage.getItem('appAdminDetails');
    const adminDetails = localStorage.getItem('adminDetails');

    if (appAdminDetails) {
      const parsedAppAdmin = JSON.parse(appAdminDetails);
      setIsAppAdmin(parsedAppAdmin.isAppAdmin || false);
      setIsCustomerAdmin(false);
      setUserDetails({
        username: parsedAppAdmin.username || 'App Admin',
        role: 'admin',
        isAdmin: true
      });
    } else if (adminDetails) {
      const parsedAdmin = JSON.parse(adminDetails);
      setIsAppAdmin(false);
      setIsCustomerAdmin(!!parsedAdmin.customerId);
      setUserDetails({
        username: parsedAdmin.username || 'Admin',
        firstName: parsedAdmin.firstName || '',
        role: parsedAdmin.role || 'sso_admin',
        isAdmin: parsedAdmin.isAdmin || false
      });

      // Fetch customer config for feature flags
      apiService.getCurrentCustomer().then(res => {
        if (res?.data?.customer) {
          setCustomerConfig(res.data.customer);
        }
      }).catch(() => {});
    }

    // Check for impersonation
    const impersonatingData = localStorage.getItem('impersonating');
    if (impersonatingData) {
      try {
        setImpersonation(JSON.parse(impersonatingData));
      } catch {}
    }
  }, []);

  // Auto-expand preferences if on a preferences page
  useEffect(() => {
    const preferencePaths = ['/replication', '/admin-management', '/identity-providers', '/scim-configuration', '/api-tokens'];
    if (preferencePaths.some(path => location.pathname === path)) {
      setPreferencesOpen(true);
    }
  }, [location.pathname]);

  // Auto-expand logs if on a logs page
  useEffect(() => {
    const logsPaths = ['/audit-logs', '/access-logs'];
    if (logsPaths.some(path => location.pathname === path)) {
      setLogsOpen(true);
    }
  }, [location.pathname]);

  const { username, firstName, role, isAdmin } = userDetails;

  const getNavigationItems = () => {
    if (isAppAdmin) {
      const appAdminItems = [
        {
          name: 'Customer Management',
          icon: <PeopleIcon />,
          path: '/customers',
        },
      ];
      // Analytics only visible in SaaS deployment mode
      if ((window.env?.DEPLOYMENT_MODE || 'self-hosted') === 'saas') {
        appAdminItems.push({
          name: 'Analytics',
          icon: <AssessmentIcon />,
          path: '/analytics',
        });
      }
      return appAdminItems;
    } else {
      const items = [
        {
          name: 'Dashboard',
          icon: <DashboardIcon />,
          path: '/dashboard',
        },
        {
          name: 'Users',
          icon: <PeopleIcon />,
          path: '/users',
        },
        {
          name: 'Groups',
          icon: <GroupIcon />,
          path: '/groups',
        },
        {
          name: 'Permission Sets',
          icon: <SecurityIcon />,
          path: '/permission-sets',
        },
        {
          name: 'Customer Managed Policies',
          icon: <ArticleIcon />,
          path: '/customer-managed-policies',
        },
        {
          name: 'Assignments',
          icon: <AssignmentIcon />,
          path: '/permission-set-assignments',
        },
        {
          name: 'Accounts',
          icon: <CloudIcon />,
          path: '/accounts',
        },
        {
          name: 'Custom Applications',
          icon: <AppsIcon />,
          path: '/custom-applications',
        },
      ];

      // Policies - only visible if enabled for this customer
      if (customerConfig?.enableOrgServices) {
        items.push({
          name: 'Policies',
          icon: <AccountTreeIcon />,
          path: '/org-services',
        });
      }

      return items;
    }
  };

  const navigationItems = getNavigationItems();

  // Get preferences sub-items based on user context
  const getPreferencesItems = () => {
    const items = [];

    // Identity Providers - only for customer admins
    if (isCustomerAdmin) {

      // Admin Management - for customer admins
      items.push({
        name: 'Admin Management',
        icon: <AdminIcon />,
        path: '/admin-management',
      });

      items.push({
        name: 'Identity Providers',
        icon: <SecurityIcon />,
        path: '/identity-providers',
      });

      // SCIM Configuration - only for customer admins
      items.push({
        name: 'SCIM',
        icon: <VpnKeyIcon />,
        path: '/scim-configuration',
      });

      // API Tokens - only for customer admins
      items.push({
        name: 'API Tokens',
        icon: <VpnKeyIcon />,
        path: '/api-tokens',
      });

      // Replication - for customer admins
      items.push({
        name: 'Replication',
        icon: <SyncIcon />,
        path: '/replication',
      });

      // Log Export - for customer admins
      items.push({
        name: 'Log Export',
        icon: <CloudUploadIcon />,
        path: '/log-export-settings',
      });

      // CloudKeeper Link - only for self-hosted instances
      if ((window.env?.DEPLOYMENT_MODE || 'self-hosted') !== 'saas') {
        items.push({
          name: 'CloudKeeper Link',
          icon: <LinkIcon />,
          path: '/saas-connection',
        });
      }
    }

    return items;
  };

  const preferencesItems = getPreferencesItems();

  // Get logs menu items - only for customer admins
  const getLogsItems = () => {
    const items = [];
    if (isCustomerAdmin) {
      items.push({
        name: 'Audit Logs',
        icon: <HistoryIcon />,
        path: '/audit-logs',
      });
      items.push({
        name: 'Access Logs',
        icon: <LoginIcon />,
        path: '/access-logs',
      });
    }
    return items;
  };

  const logsItems = getLogsItems();

  const isLogsActive = () => {
    return logsItems.some(item => location.pathname === item.path);
  };

  const handleLogsClick = () => {
    if (isCollapsed) {
      // If collapsed, expand sidebar first
      setIsCollapsed(false);
      setLogsOpen(true);
    } else {
      setLogsOpen(!logsOpen);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('Logout error:', error);
      if (onLogout) {
        onLogout();
      }
    }
  };

  const isActive = (path) => {
    if (location.pathname === path) return true;
    // Also match detail sub-pages (e.g. /accounts/123456789/contacts)
    if (path !== '/' && location.pathname.startsWith(path + '/')) return true;
    return false;
  };

  const isPreferencesActive = () => {
    return preferencesItems.some(item => location.pathname === item.path);
  };

  const handlePreferencesClick = () => {
    if (isCollapsed) {
      // If collapsed, expand sidebar first
      setIsCollapsed(false);
      setPreferencesOpen(true);
    } else {
      setPreferencesOpen(!preferencesOpen);
    }
  };

  const sidebarStagger = {
    initial: {},
    animate: { transition: { staggerChildren: 0.04, delayChildren: 0.1 } },
  };
  const sidebarItem = {
    initial: { opacity: 0, x: -8 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={true}
      className="modern-sidebar-drawer"
      sx={{
        width: isCollapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isCollapsed ? collapsedWidth : drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E5E5E5',
          borderRadius: 0,
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          overflowY: 'hidden',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1200,
        },
      }}
    >
      {/* Header Section */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, ease: 'easeOut' }}>
      <Paper
        elevation={0}
        className="sidebar-header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          padding: isCollapsed ? '16px 8px' : '16px 20px',
          minHeight: 72,
          maxHeight: 72,
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E5E5',
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        {!isCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="https://www.cloudkeeper.com/themes/cloud_theme/images/Cloudkeeper_New.svg"
              alt="CloudKeeper"
              style={{
                height: '32px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </Box>
        )}
        <Tooltip title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}>
          <IconButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="collapse-button"
            sx={{
              color: '#253E66',
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: '#F0F4F8',
                borderColor: '#D9DBDC',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Paper>
      </motion.div>

      {/* Navigation List */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          py: 2,
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F9FBFD',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#D9DBDC',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#BDBDBD',
          },
        }}
      >
        <motion.div variants={sidebarStagger} initial="initial" animate="animate" style={{ padding: 0, margin: 0, listStyle: 'none' }}>
          {navigationItems.map((item) => (
            <motion.div key={item.path} variants={sidebarItem}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={isCollapsed ? item.name : ''} placement="right">
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={isActive(item.path)}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                  sx={{
                    borderRadius: '6px',
                    color: isActive(item.path) ? '#4698D3' : '#666666',
                    backgroundColor: isActive(item.path) ? 'rgba(70, 152, 211, 0.08)' : 'transparent',
                    border: isActive(item.path) ? '1px solid rgba(70, 152, 211, 0.2)' : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: isActive(item.path)
                        ? 'rgba(70, 152, 211, 0.12)'
                        : '#F9FBFD',
                      color: isActive(item.path) ? '#4698D3' : '#253E66',
                    },
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    px: isCollapsed ? 1.5 : 2,
                    py: 1.25,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'inherit',
                      minWidth: isCollapsed ? 'auto' : 36,
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem',
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={item.name}
                      primaryTypographyProps={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: isActive(item.path) ? 600 : 500,
                        fontSize: '0.875rem',
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            </motion.div>
          ))}

          {/* Logs Dropdown - only show for customer admins */}
          {logsItems.length > 0 && (
            <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: 0.1 + navigationItems.length * 0.04, ease: [0.22, 1, 0.36, 1] }}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={isCollapsed ? 'Logs' : ''} placement="right">
                  <ListItemButton
                    onClick={handleLogsClick}
                    className={`nav-item ${isLogsActive() ? 'active' : ''}`}
                    sx={{
                      borderRadius: '6px',
                      color: isLogsActive() ? '#5D6D7E' : '#666666',
                      backgroundColor: isLogsActive() ? 'rgba(93, 109, 126, 0.08)' : 'transparent',
                      border: isLogsActive() ? '1px solid rgba(93, 109, 126, 0.2)' : '1px solid transparent',
                      '&:hover': {
                        backgroundColor: isLogsActive()
                          ? 'rgba(93, 109, 126, 0.12)'
                          : '#F9FBFD',
                        color: isLogsActive() ? '#5D6D7E' : '#253E66',
                      },
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      px: isCollapsed ? 1.5 : 2,
                      py: 1.25,
                      minHeight: 44,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: 'inherit',
                        minWidth: isCollapsed ? 'auto' : 36,
                        justifyContent: 'center',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.25rem',
                        },
                      }}
                    >
                      <AssessmentIcon />
                    </ListItemIcon>
                    {!isCollapsed && (
                      <>
                        <ListItemText
                          primary="Logs"
                          primaryTypographyProps={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: isLogsActive() ? 600 : 500,
                            fontSize: '0.875rem',
                          }}
                        />
                        {logsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {/* Logs Sub-items */}
              <Collapse in={logsOpen && !isCollapsed} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 2 }}>
                  {logsItems.map((item, index) => (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
                    >
                    <ListItem disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => navigate(item.path)}
                        selected={isActive(item.path)}
                        sx={{
                          borderRadius: '6px',
                          color: isActive(item.path) ? '#5D6D7E' : '#666666',
                          backgroundColor: isActive(item.path) ? 'rgba(93, 109, 126, 0.08)' : 'transparent',
                          border: isActive(item.path) ? '1px solid rgba(93, 109, 126, 0.15)' : '1px solid transparent',
                          '&:hover': {
                            backgroundColor: isActive(item.path)
                              ? 'rgba(93, 109, 126, 0.12)'
                              : '#F9FBFD',
                            color: isActive(item.path) ? '#5D6D7E' : '#253E66',
                          },
                          py: 0.75,
                          px: 1.5,
                          minHeight: 36,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: 'inherit',
                            minWidth: 28,
                            justifyContent: 'center',
                            '& .MuiSvgIcon-root': {
                              fontSize: '1rem',
                            },
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: isActive(item.path) ? 600 : 500,
                            fontSize: '0.8rem',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    </motion.div>
                  ))}
                </List>
              </Collapse>
            </motion.div>
          )}

          {/* Preferences Dropdown - only show for customer admins */}
          {preferencesItems.length > 0 && (
            <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: 0.1 + (navigationItems.length + 1) * 0.04, ease: [0.22, 1, 0.36, 1] }}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={isCollapsed ? 'Preferences' : ''} placement="right">
                  <ListItemButton
                    onClick={handlePreferencesClick}
                    className={`nav-item ${isPreferencesActive() ? 'active' : ''}`}
                    sx={{
                      borderRadius: '6px',
                      color: isPreferencesActive() ? '#5D6D7E' : '#666666',
                      backgroundColor: isPreferencesActive() ? 'rgba(93, 109, 126, 0.08)' : 'transparent',
                      border: isPreferencesActive() ? '1px solid rgba(93, 109, 126, 0.2)' : '1px solid transparent',
                      '&:hover': {
                        backgroundColor: isPreferencesActive()
                          ? 'rgba(93, 109, 126, 0.12)'
                          : '#F9FBFD',
                        color: isPreferencesActive() ? '#5D6D7E' : '#253E66',
                      },
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      px: isCollapsed ? 1.5 : 2,
                      py: 1.25,
                      minHeight: 44,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: 'inherit',
                        minWidth: isCollapsed ? 'auto' : 36,
                        justifyContent: 'center',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.25rem',
                        },
                      }}
                    >
                      <TuneIcon />
                    </ListItemIcon>
                    {!isCollapsed && (
                      <>
                        <ListItemText
                          primary="Preferences"
                          primaryTypographyProps={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: isPreferencesActive() ? 600 : 500,
                            fontSize: '0.875rem',
                          }}
                        />
                        {preferencesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {/* Preferences Sub-items */}
              <Collapse in={preferencesOpen && !isCollapsed} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 2 }}>
                  {preferencesItems.map((item, index) => (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
                    >
                    <ListItem disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => navigate(item.path)}
                        selected={isActive(item.path)}
                        sx={{
                          borderRadius: '6px',
                          color: isActive(item.path) ? '#5D6D7E' : '#666666',
                          backgroundColor: isActive(item.path) ? 'rgba(93, 109, 126, 0.08)' : 'transparent',
                          border: isActive(item.path) ? '1px solid rgba(93, 109, 126, 0.15)' : '1px solid transparent',
                          '&:hover': {
                            backgroundColor: isActive(item.path)
                              ? 'rgba(93, 109, 126, 0.12)'
                              : '#F9FBFD',
                            color: isActive(item.path) ? '#5D6D7E' : '#253E66',
                          },
                          py: 0.75,
                          px: 1.5,
                          minHeight: 36,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: 'inherit',
                            minWidth: 28,
                            justifyContent: 'center',
                            '& .MuiSvgIcon-root': {
                              fontSize: '1rem',
                            },
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: isActive(item.path) ? 600 : 500,
                            fontSize: '0.8rem',
                            color: 'inherit',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    </motion.div>
                  ))}
                </List>
              </Collapse>
            </motion.div>
          )}
        </motion.div>
      </Box>

      {/* Bottom section */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
      <Box sx={{
        flexShrink: 0,
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#FFFFFF',
        zIndex: 1,
      }}>
        {/* Impersonation Banner */}
        {impersonation && !isCollapsed && (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFF3E0',
              borderTop: '1px solid #FFB74D',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.7rem', color: '#E65100', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Impersonating
              </Typography>
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.8rem', color: '#BF360C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {impersonation.customerName}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('adminDetails');
                localStorage.removeItem('impersonating');
                localStorage.removeItem('customerId');
                // Redirect back to app admin portal
                // Current host is e.g. internal.prism.cloudkeeper.com — strip the realm prefix
                const protocol = window.location.protocol;
                const host = window.location.hostname;
                const port = window.location.port ? `:${window.location.port}` : '';
                const baseDomain = host.split('.').slice(1).join('.');
                window.location.href = `${protocol}//${baseDomain}${port}/customers`;
              }}
              sx={{
                color: '#E65100',
                backgroundColor: 'rgba(230, 81, 0, 0.08)',
                borderRadius: '6px',
                '&:hover': { backgroundColor: 'rgba(230, 81, 0, 0.15)' },
              }}
            >
              <LogoutIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Paper>
        )}
        {/* User Section */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#F9FBFD',
            borderTop: '1px solid #E5E5E5',
            padding: isCollapsed ? '12px 8px' : '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'space-between',
            borderRadius: 0,
            minHeight: 68,
            maxHeight: 68,
          }}
        >
          {!isCollapsed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: '#4698D3',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  flexShrink: 0,
                }}
              >
                {(firstName || username).charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography
                  sx={{
                    color: '#253E66',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {firstName || username}
                </Typography>
                <Typography
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.75rem',
                    lineHeight: 1,
                    fontWeight: 500,
                  }}
                >
                  {hasMinRole(role, ROLES.ADMIN) ? "Super Admin" : isReadOnly(role) ? "Read-only" : (isAdmin ? "Admin" : "User")}
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Tooltip title="Logout" placement="top">
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#E74C3C',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    backgroundColor: 'rgba(231, 76, 60, 0.08)',
                    borderColor: '#E74C3C',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <LogoutIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Box>
      </motion.div>
    </Drawer>
  );
};

export default Sidebar;
